import { v4 as uuidv4 } from 'uuid';

import { SlDataService } from '../sl-data.service';
import { SlLEContext } from '../../global-constants';
import {
	ITrackActionData,
	LeaderboardClickTypes,
	MaterialQuitAction,
	QuizFeedbackType,
	TrackEventName,
} from '../../global-interface';
import { IMaterialInfo, MaterialType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import {
	Service, Container, TrackingService, LearningExperienceType, track, TrackingEventNames, DataService,
} from '../../../../symphony';
import { getMaterialFromCourse, getMaterialFromPath } from '../../utils/helpers';

@Service()
export class SlTrackingService {
	private data = Container.take(SlLEContext, SlDataService);

	private trackingService = Container.take('global', TrackingService);

	private globalData = Container.take('global', DataService);

	private getLPMaterialHeader = () => {
		const { lesson$, lessonPageIndex$ } = this.data;
		return lesson$.value.children[lessonPageIndex$.value - 1]?.materialInfo
			.header;
	};

	public onMaterialClick = async ({ relationId }: ITrackActionData): Promise<void> => {
		let currentMaterial: IMaterialInfo;

		const {
			course$, learningPath$, lesson$, lessonPageIndex$,
		} = this.data;

		const type = await this.globalData.getLETypeAsync();

		if (lesson$.value) {
			currentMaterial = lesson$.value.children[
				lessonPageIndex$.value - 1
			].children.find(
				(el) => el.materialInfo.header.materialRelationId === relationId,
			).materialInfo;
		} else if (type === LearningExperienceType.LearningPath) {
			currentMaterial = getMaterialFromPath(learningPath$.value, relationId);
		} else if (type === LearningExperienceType.Course) {
			currentMaterial = getMaterialFromCourse(course$.value, relationId);
		}

		if (currentMaterial) {
			const trackData = {
				event_name: TrackEventName.MaterialClick,
				dynamic_id: String(currentMaterial.header.dynamicId || ''),
				material_id: String(currentMaterial.header.materialId || ''),
				static_id: String(currentMaterial.header.staticId || ''),
				material_type_id: String(currentMaterial.header.typeId),
				completion_status: String(currentMaterial.status.completion),
				visibility_status: String(currentMaterial.status.visibility),
				experience_type_id: String(type),
				experience_alias: this.data.alias$.value,
				is_owned: currentMaterial.status.ownership.isOwned,
				ownership_requirement_type_id: String(currentMaterial.status.ownership.ownershipRequirementsTypeId),
				bundle_id: await this.data.getBundleIdAsync(),
				version: '6-0-0',
			};
			track(trackData);
		} else {
			// TODO check and remove
			console.error(`Material with relationId = ${relationId} has not been found`);
		}
	};

	public onMaterialMount = async ({ materialInfo }: ITrackActionData): Promise<void> => {
		const trackData = {
			event_name: TrackEventName.MaterialImpression,
			dynamic_id: String(materialInfo.header.dynamicId || ''),
			material_id: String(materialInfo.header.materialId || ''),
			static_id: String(materialInfo.header.staticId || ''),
			material_type_id: String(materialInfo.header.typeId),
			experience_type_id: String(await this.globalData.getLETypeAsync()),
			experience_alias: this.data.alias$.value,
			bundle_id: await this.data.getBundleIdAsync(),
			page_id: '',
			version: '5-0-0',
		};
		track(trackData);
	};

	public materialQuit = async ({ quitAction }: ITrackActionData): Promise<void> => {
		const materialHeader = this.getLPMaterialHeader();

		const trackData = {
			event_name: TrackEventName.MaterialQuit,
			dynamic_id: String(materialHeader?.dynamicId || ''),
			material_id: String(materialHeader?.materialId || ''),
			static_id: String(materialHeader?.staticId || ''),
			material_type_id: String(MaterialType.Lesson),
			experience_type_id: String(await this.globalData.getLETypeAsync()),
			experience_alias: this.data.alias$.value,
			action: quitAction,
			bundle_id: await this.data.getBundleIdAsync(),
			version: '4-0-0',
		};
		track(trackData);
	};

	public materialCTAClick = async ({
		materialCTAClickType,
	}: ITrackActionData): Promise<void> => {
		const { quizFeedback$ } = this.data;
		const materialHeader = this.getLPMaterialHeader();

		const trackData = {
			event_name: TrackEventName.MaterialCTAClick,
			dynamic_id: String(materialHeader?.dynamicId || ''),
			material_id: String(materialHeader?.materialId || ''),
			static_id: String(materialHeader?.staticId || ''),
			material_type_id: String(MaterialType.LessonPage),
			experience_type_id: String(await this.globalData.getLETypeAsync()),
			experience_alias: this.data.alias$.value,
			bundle_id: await this.data.getBundleIdAsync(),
			page_id: '3', // Lesson
			cta_type_id: materialCTAClickType,
			version: '5-0-0',
			is_correct:
				quizFeedback$.value
				&& quizFeedback$.value === QuizFeedbackType.Correct,
		};

		track(trackData);
	};

	public materialSolutionClick = async (): Promise<void> => {
		const { question$ } = this.data;

		const trackData = {
			event_name: TrackEventName.MaterialSolutionClick,
			dynamic_id: String(question$.value.header.dynamicId || ''),
			material_id: String(question$.value.header.materialId || ''),
			static_id: String(question$.value.header.staticId || ''),
			material_type_id: String(MaterialType.Question),
			bundle_id: await this.data.getBundleIdAsync(),
			version: '4-0-0',
		};

		track(trackData);
	};

	public materialSolutionImpression = async (): Promise<void> => {
		const { question$ } = this.data;

		const trackData = {
			event_name: TrackEventName.MaterialSolutionImpression,
			dynamic_id: String(question$.value.header.dynamicId || ''),
			material_id: String(question$.value.header.materialId || ''),
			static_id: String(question$.value.header.staticId || ''),
			material_type_id: String(MaterialType.Question),
			bundle_id: await this.data.getBundleIdAsync(),
			version: '4-0-0',
		};

		track(trackData);
	};

	public commentClick = (): void => {
		const { lesson$, lessonPageIndex$ } = this.data;
		const materialHeader = this.getLPMaterialHeader();

		const { commentContainerId } = lesson$.value.children[lessonPageIndex$.value - 1].materialInfo;

		const trackData = {
			event_name: TrackEventName.CommentClick,
			dynamic_id: String(materialHeader?.dynamicId || ''),
			material_id: String(materialHeader?.materialId || ''),
			static_id: String(materialHeader?.staticId || ''),
			material_type_id: String(MaterialType.LessonPage),
			comments_container_id: String(commentContainerId),
			version: '4-0-0',
		};

		track(trackData);
	};

	public certificateCTAClick = async (): Promise<void> => {
		const trackData = {
			event_name: TrackEventName.CertificateCTAClick,
			relation_id: await this.data.getCourseIdAsync(),
			bundle_id: await this.data.getBundleIdAsync(),
			experience_alias: this.data.alias$.value,
			version: '2-0-0',
		};
		track(trackData);
	};

	public codeSnippetClick = async (): Promise<void> => {
		const materialHeader = this.getLPMaterialHeader();

		const trackData = {
			event_name: TrackEventName.CodeSnippetClick,
			version: '3-0-0',
			dynamic_id: String(materialHeader?.dynamicId || ''),
			material_id: String(materialHeader?.materialId || ''),
			static_id: String(materialHeader?.staticId || ''),
		};
		track(trackData);
	};

	public imageClick = async ({ imgUrl }: ITrackActionData): Promise<void> => {
		const materialHeader = this.getLPMaterialHeader();

		const trackData = {
			event_name: TrackEventName.ImageClick,
			dynamic_id: String(materialHeader?.dynamicId || ''),
			material_id: String(materialHeader?.materialId || ''),
			static_id: String(materialHeader?.staticId || ''),
			image_url: imgUrl,
			version: '3-0-0',
		};
		track(trackData);
	};

	public onLandingPageMount = async ({ alias }: ITrackActionData): Promise<void> => {
		// This event has a newer version '3-0-0' with required fields section_type and section_title
		const trackData = {
			event_name: TrackEventName.LearningExpLandingImpression,
			experience_type_id: String(await this.globalData.getLETypeAsync()),
			experience_alias: alias,
			version: '2-0-0',
		};
		track(trackData);
	};

	public onLandingPageCTAClick = async (): Promise<void> => {
		// This event has a newer version '3-0-0' with required fields section_type and section_title
		const trackData = {
			event_name: TrackEventName.LearningExpLandingCTAClick,
			experience_type_id: String(await this.globalData.getLETypeAsync()),
			experience_alias: this.data.alias$.value,
			cta_type_id: 'button',
			version: '2-0-0',
		};
		track(trackData);
	};

	public onCompleteFirstLessonCelebrationMount = (): void => {
		const materialHeader = this.getLPMaterialHeader();

		const trackData = {
			event_name: TrackEventName.CelebrationImpression,
			dynamic_id: String(materialHeader?.dynamicId || ''),
			material_id: String(materialHeader?.materialId || ''),
			static_id: String(materialHeader?.staticId || ''),
			material_type_id: String(MaterialType.Lesson),
			id: uuidv4(),
			order_number: this.data.lesson$.value.materialInfo.orderNumber,
			version: '3-0-0',
		};
		track(trackData);
	};

	public onCompleteFirstLessonCelebrationClick = (): void => {
		const materialHeader = this.getLPMaterialHeader();

		const trackData = {
			event_name: TrackEventName.CelebrationClick,
			dynamic_id: String(materialHeader?.dynamicId || ''),
			material_id: String(materialHeader?.materialId || ''),
			static_id: String(materialHeader?.staticId || ''),
			material_type_id: String(MaterialType.Lesson),
			id: uuidv4(),
			click_type: 'CONTINUE',
			order_number: this.data.lesson$.value.materialInfo.orderNumber,
			version: '3-0-0',
		};
		track(trackData);
	};

	public onLeaderboardScreenContinueClick = (): void => {
		const trackData = {
			event_name: TrackEventName.LeaderboardAfterLessonCompletionClick,
			id: uuidv4(),
			click_type: LeaderboardClickTypes.Continue,
			completed_lesson_order: this.data.lessonOrderInCourse$.value,
			version: '2-0-0',
		};
		track(trackData);
	};

	public onLeaderboardScreenGoToLeaderboardClick = (): void => {
		const trackData = {
			event_name: TrackEventName.LeaderboardAfterLessonCompletionClick,
			id: uuidv4(),
			click_type: LeaderboardClickTypes.GoToLeaderboard,
			completed_lesson_order: this.data.lessonOrderInCourse$.value,
			version: '2-0-0',
		};
		track(trackData);
	};

	public onLeaderboardScreenImpression = (): void => {
		const trackData = {
			event_name: TrackEventName.LeaderboardAfterLessonCompletionImpression,
			id: uuidv4(),
			completed_lesson_order: this.data.lessonOrderInCourse$.value,
			version: '2-0-0',
		};
		track(trackData);
	};

	public lessonQuitPromptImpression = (): void => {
		const materialHeader = this.getLPMaterialHeader();

		const trackData = {
			version: '2-0-0',
			id: uuidv4(),
			event_name: TrackEventName.LessonQuitPopupImpression,
			dynamic_id: String(materialHeader?.dynamicId || ''),
			material_id: String(materialHeader?.materialId || ''),
			static_id: String(materialHeader?.staticId || ''),
			quit_type: MaterialQuitAction.XButton,
		};
		track(trackData);
	};

	public lessonQuitPromptClick = ({ quitLessonClickType }: ITrackActionData): void => {
		const materialHeader = this.getLPMaterialHeader();

		const trackData = {
			version: '2-0-0',
			id: uuidv4(),
			event_name: TrackEventName.LessonQuitPopupClick,
			dynamic_id: String(materialHeader?.dynamicId || ''),
			material_id: String(materialHeader?.materialId || ''),
			static_id: String(materialHeader?.staticId || ''),
			click_type: quitLessonClickType,
			quit_type: MaterialQuitAction.XButton,
		};
		track(trackData);
	};

	public onStreakGoalScreenMount = (): void => {
		const trackData = {
			event_name: TrackEventName.UserStreakGoalPopupImpression,
		};
		track(trackData);
	};

	public onCoursesMount = (): void => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.courseCatalogImpression,
			eventParams: {
				pageAlias: 'CATALOG',
			},
		});
	};

	public onShowOriginalButtonClick = (): void => {
		const materialHeader = this.getLPMaterialHeader();

		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.dynamicTranslationCTAClick,
			eventParams: {
				clickType: 'SHOW ORIGINAL',
				dynamicId: String(materialHeader?.dynamicId || ''),
				materialId: String(materialHeader?.materialId || ''),
				staticId: String(materialHeader?.staticId || ''),
				version: '2-0-0',
			},
		});
	};

	public onDynamicTranslationTipCTAClick = ({
		isTranslated,
	}: ITrackActionData): void => {
		const materialHeader = this.getLPMaterialHeader();

		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.dynamicTranslationTipCTAClick,
			eventParams: {
				clickType: isTranslated ? 'SEE ORIGINAL' : 'TRANSLATE',
				dynamicId: String(materialHeader?.dynamicId || ''),
				materialId: String(materialHeader?.materialId || ''),
				staticId: String(materialHeader?.staticId || ''),
				version: '2-0-0',
			},
		});
	};

	public onSeeTranslationButtonClick = (): void => {
		const materialHeader = this.getLPMaterialHeader();

		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.dynamicTranslationCTAClick,
			eventParams: {
				clickType: 'SEE TRANSLATION',
				dynamicId: String(materialHeader?.dynamicId || ''),
				materialId: String(materialHeader?.materialId || ''),
				staticId: String(materialHeader?.staticId || ''),
				version: '2-0-0',
			},
		});
	};

	public onTranslateButtonClick = ({ locale }: ITrackActionData): void => {
		const materialHeader = this.getLPMaterialHeader();

		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.dynamicTranslationButtonClick,
			eventParams: {
				clickType: 'TRANSLATE',
				dynamicId: String(materialHeader?.dynamicId || ''),
				materialId: String(materialHeader?.materialId || ''),
				staticId: String(materialHeader?.staticId || ''),
				version: '2-0-0',
				language: locale,
			},
		});
	};

	public onTranslateCourseButtonClick = (): void => {
		const materialHeader = this.getLPMaterialHeader();

		this.data.isTranslationModalOpened$.next(true);
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.dynamicTranslationCTAClick,
			eventParams: {
				clickType: 'TRANSLATE',
				dynamicId: String(materialHeader?.dynamicId || ''),
				materialId: String(materialHeader?.materialId || ''),
				staticId: String(materialHeader?.staticId || ''),
				version: '2-0-0',
			},
		});
	};

	public onAICoursePaywall1Mount = (): void => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.aiPaywallsImpression,
			eventParams: {
				version: '2-0-0',
				type: 'AI_COURSE',
				source: 'LESSON_COMPLETE',
				subscriptionProductListId: ['ai-course'],
				pageType: 'MAIN_PAGE',
				appMode: 'UNDEFINED',
				appVersion: 'UNDEFINED',
				pageNumber: '1',
				experimentId: 'UNDEFINED',
				wasPro: false,
			},
		});
	};

	public onAICoursePaywall2Mount = (): void => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.aiPaywallsImpression,
			eventParams: {
				version: '2-0-0',
				type: 'AI_COURSE',
				source: 'LESSON_COMPLETE',
				subscriptionProductListId: ['ai-course'],
				pageType: 'MAIN_PAGE',
				appMode: 'UNDEFINED',
				appVersion: 'UNDEFINED',
				pageNumber: '2',
				experimentId: 'UNDEFINED',
				wasPro: false,
			},
		});
	};

	public onAICoursePaywall1CtaClick = (): void => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.aiPaywallsClick,
			eventParams: {
				version: '2-0-0',
				type: 'AI_COURSE',
				source: 'LESSON_COMPLETE',
				subscriptionProductId: 'ai-course',
				pageType: 'MAIN_PAGE',
				clickType: 'CTA',
				appMode: 'UNDEFINED',
				appVersion: 'UNDEFINED',
				pageNumber: '1',
				experimentId: 'UNDEFINED',
				wasPro: false,
			},
		});
	};

	public onAICoursePaywall2Close = (): void => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.aiPaywallsClick,
			eventParams: {
				version: '2-0-0',
				type: 'AI_COURSE',
				source: 'LESSON_COMPLETE',
				subscriptionProductId: 'ai-course',
				pageType: 'MAIN_PAGE',
				clickType: 'CLOSE',
				appMode: 'UNDEFINED',
				appVersion: 'UNDEFINED',
				pageNumber: '2',
				experimentId: 'UNDEFINED',
				wasPro: false,
			},
		});
	};

	public onAICoursePaywall2CtaClick = (): void => {
		this.trackingService.tracking$.next({
			eventName: TrackingEventNames.aiPaywallsClick,
			eventParams: {
				version: '2-0-0',
				type: 'AI_COURSE',
				source: 'LESSON_COMPLETE',
				subscriptionProductId: 'ai-course',
				pageType: 'MAIN_PAGE',
				clickType: 'CTA',
				appMode: 'UNDEFINED',
				appVersion: 'UNDEFINED',
				pageNumber: '2',
				experimentId: 'UNDEFINED',
				wasPro: false,
			},
		});
	};
}
